import axios, { AxiosInstance } from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:7000/api";

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("token");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid
          localStorage.removeItem("token");
          window.location.href = "/login";
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async login(email: string, password: string, two_fa_preference?: string) {
    const response = await this.api.post("/auth/sign-in", {
      email,
      password,
      ...(two_fa_preference && { two_fa_preference }),
    });
    return response.data;
  }

  async verifyOtpSignIn(
    endpoint: string,
    email: string,
    password: string,
    otp?: string
  ) {
    const response = await this.api.post(endpoint, {
      email,
      password,
      otp,
    });
    return response.data;
  }

  async signup(data: any) {
    const response = await this.api.post("/auth/sign-up", data);
    return response.data;
  }

  async verifyOtpSignup(email: string, otp: string) {
    const response = await this.api.post("/auth/verify-otp/sign-up/email", {
      email,
      otp,
    });
    return response.data;
  }

  async forgotPassword(email: string) {
    const response = await this.api.post("/auth/forgot-password", { email });
    return response.data;
  }

  async forgotPasswordVerifyOTP(
    email: string,
    newPassword: string,
    otpValue: string
  ) {
    const response = await this.api.post(
      "/auth/verify-otp/forgot-password/email",
      {
        email,
        password: newPassword,
        otp: otpValue,
      }
    );
    return response.data;
  }

  // Reference endpoints
  async getClinics(params: any) {
    const response = await this.api.get("/references/clinics/list", {
      params: { ...params },
    });
    return response.data;
  }

  async getProductsList() {
    const response = await this.api.get("/references/products/list");
    return response.data;
  }

  async getExternalClinics() {
    const response = await this.api.get("/references/external/clinics/list", {
      params: { is_all: 1 },
    });
    return response.data;
  }

  async getExternalPatientsList(params: any) {
    const response = await this.api.get(`/references/external/patients/list`, {
      params: { ...params },
    });
    return response.data;
  }

  async getClinicsWhiteLabelSettings(params: any) {
    const response = await this.api.get(
      `/references/clinics/white-label-settings`,
      {
        params: { ...params },
      }
    );
    return response.data;
  }

  // User endpoints
  async getUserDetails() {
    const response = await this.api.get("/v1/users/details");
    return response.data;
  }

  async changePassword(oldPassword: string, newPassword: string) {
    const response = await this.api.post("/v1/users/change-password", {
      old_password: oldPassword,
      new_password: newPassword,
    });
    return response.data;
  }

  async changeTwoFAPreference(preference: "email" | "authenticator") {
    const response = await this.api.post("/v1/users/change-tfa-preference", {
      two_fa_preference: preference,
    });
    return response.data;
  }

  //
  async getPatientProducts(currentPage?: number, pageSize?: number) {
    const response = await this.api.get("/v1/product/patient/search", {
      params: {
        ...(currentPage && { page: currentPage }),
        ...(pageSize && { pageSize: pageSize }),
      },
    });
    return response.data;
  }

  // Patient endpoints
  async getPatients(clinicId?: string) {
    const response = await this.api.get("/patients", { params: { clinicId } });
    return response.data;
  }

  async createPatient(data: any) {
    const response = await this.api.post("/patients", data);
    return response.data;
  }

  async updatePatient(id: string, data: any) {
    const response = await this.api.put(`/patients/${id}`, data);
    return response.data;
  }

  async deletePatient(id: string) {
    const response = await this.api.delete(`/patients/${id}`);
    return response.data;
  }

  // Product endpoint for admin
  async createProduct(data: any) {
    const response = await this.api.post("/v1/product/create", data);
    return response.data;
  }

  async searchProducts(params: any) {
    const response = await this.api.get("/v1/product/search", {
      params: {
        ...params,
      },
    });
    return response.data;
  }

  async updateProduct(data: any) {
    const response = await this.api.put(`/v1/product/update`, data);
    return response.data;
  }

  async deleteProduct(data: any) {
    const response = await this.api.delete(`/v1/product/delete`, { data });
    return response.data;
  }

  // Order endpoints
  async createOrder(data: any) {
    const response = await this.api.post("/v1/orders/payment-submit", data);
    return response.data;
  }

  async getOrders(params?: any) {
    const response = await this.api.get("/v1/orders/search", { params });
    return response.data;
  }

  async getOrderByProductID(product_id?: number) {
    const response = await this.api.get("/v1/orders/search", {
      params: { product_id },
    });
    return response.data;
  }

  async getOrderReceipt(order_id?: string) {
    const response = await this.api.post(
      "/v1/orders/generate-receipt/pdf",
      {
        order_id: order_id,
      },
      { responseType: "blob" }
    );

    return response.data;
  }

  async getPatientOrderReceipt(product_id?: number) {
    const response = await this.api.post(
      "/v1/orders/generate-receipt/pdf",
      {
        product_id: product_id,
      },
      { responseType: "blob" }
    );

    return response.data;
  }

  // Invoice endpoints
  async getInvoices(params?: any) {
    const response = await this.api.get("/v1/invoice/search", { params });
    return response.data;
  }

  async getDetailsByInvoiceNumber(invoiceNumber: number) {
    const response = await this.api.get(
      `/v1/invoice/details/${invoiceNumber}`,
      { params: {} }
    );
    return response.data;
  }

  async getInvoiceReceipt(invoiceNumber: number) {
    const response = await this.api.post(
      "/v1/invoice/generate-receipt/pdf",
      {
        invoice_number: invoiceNumber,
      },
      { responseType: "blob" }
    );

    return response.data;
  }

  async resendInvoiceOnEmail(invoiceNumber: number) {
    const response = await this.api.post("/v1/invoice/resend", {
      invoice_number: invoiceNumber,
    });
    return response.data;
  }

  // User management endpoints for admin
  async createUser(data: any) {
    const response = await this.api.post("/v1/users/admin/create", data);
    return response.data;
  }

  async updateUser(data: any) {
    const response = await this.api.put("/v1/users/admin/update", data);
    return response.data;
  }

  async searchUsers(params: any = {}) {
    const response = await this.api.get("/v1/users/admin/search", {
      params: {
        ...params,
      },
    });
    return response.data;
  }

  async updateUserStatus(userId: number, action: "activate" | "deactivate") {
    const response = await this.api.patch("/v1/users/admin/status/action", {
      user_id: userId,
      action: action,
    });
    return response.data;
  }

  // Clinic management endpoints for admin
  async getClinicOverview() {
    const response = await this.api.get("/v1/clinic/overview");
    return response.data;
  }

  async onboardClinic(data: any) {
    const response = await this.api.post("/v1/clinic/onboard", data);
    return response.data;
  }

  async updateClinic(data: any) {
    const response = await this.api.put("/v1/clinic/update", data);
    return response.data;
  }

  async searchClinics(params: any) {
    const response = await this.api.get("/v1/clinic/search", {
      params: {
        ...params,
      },
    });
    return response.data;
  }

  async updateClinicStatus(
    clinicId: number,
    action: "activate" | "deactivate"
  ) {
    const response = await this.api.patch("/v1/clinic/status/action", {
      clinic_id: clinicId,
      action: action,
    });
    return response.data;
  }

  // Clinic user management endpoints  for admin
  async createClinicUser(data: any) {
    const response = await this.api.post("/v1/clinic/users/add", data);
    return response.data;
  }

  async updateClinicUser(data: any) {
    const response = await this.api.put("/v1/clinic/users/update", data);
    return response.data;
  }

  async searchClinicUser(params: any) {
    const response = await this.api.get("/v1/clinic/users/search", {
      params: {
        ...params,
      },
    });
    return response.data;
  }

  async updateClinicUserStatus(data: any) {
    const response = await this.api.patch(
      "/v1/clinic/users/status/action",
      data
    );
    return response.data;
  }

  // Clinic product management endpoints  for admin
  async upsertClinicProducts(data: any) {
    const response = await this.api.post(`/v1/clinic/products/upsert`, data);
    return response.data;
  }

  async searchClinicProducts(params: any) {
    const response = await this.api.get(`/v1/clinic/products/search`, {
      params: {
        ...params,
      },
    });

    return response.data;
  }

  async deleteClinicProduct(data: any) {
    const response = await this.api.delete(`/v1/clinic/products/delete`, {
      data,
    });
    return response.data;
  }

  // Clinic patient management endpoints  for admin
  async searchClinicPatients(params: any) {
    const response = await this.api.get(`/v1/clinic/patients/search`, {
      params: {
        ...params,
      },
    });

    return response.data;
  }

  async searchClinicPatientsProducts(params: any) {
    const response = await this.api.get(`/v1/clinic/patients/products/search`, {
      params: {
        ...params,
      },
    });

    return response.data;
  }

  async upsertPatientProducts(data: any) {
    const response = await this.api.put(
      `/v1/clinic/patients/products/upsert`,
      data
    );
    return response.data;
  }

  // Update patient status (activate/deactivate)
  async updatePatientStatus(data: any) {
    const response = await this.api.patch(
      `/v1/clinic/patients/status/action`,
      data
    );
    return response.data;
  }

  // Update patient approval (approve/reject)
  async updatePatientApproval(data: any) {
    const response = await this.api.put(
      `/v1/clinic/patients/approval/action`,
      data
    );
    return response.data;
  }

  // Dashboard endpoints
  async recentActivity(params: any) {
    const response = await this.api.get(`/v1/dashboard/recent-activity`, {
      params: {
        ...params,
      },
    });
    return response.data;
  }
  async overview(params: any) {
    const response = await this.api.get(`/v1/dashboard/overview`, {
      params: {
        ...params,
      },
    });
    return response.data;
  }
}

export default new ApiService();
